
import { useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import * as invoicingApi from '../api/Invoicing';
import { getCompanies } from '../api/Company';
import { getSettings } from '../api/Settings';
import { ICompany, INVOICING_LIMIT, IPomReportDataInfo, IPomReportSummaryInfo, REPORTSTATUS } from '../util/DataModelInterface'
import { DataTable } from '../components/DataTable';
import { statusView } from '../components/CommonControls';
import { actionButtons } from '../components/ActionButtons';
import { IFilterButtonData } from '../components/TableFilters';
import { Spinner } from '../components/Spinner';
import { ToastWrap } from '../components/ToastWrap';
import useToastHandler from '../hooks/useToastHandler';
import { ConfirmModal } from '../components/ConfirmModal';
import { getPeriodString, getDeadline, formatFinCurrency } from '../util/FormatDateUtil';

interface IBookkeepingSubrowData {
  company: string,
  period: string,
  state: JSX.Element,
  sum: string,
  billed: JSX.Element[], 
  actions: JSX.Element,
  id: string,
  status: string,
  periodTimestamp: string,
  companyId: string,
  invoicinglimit: string,
}

interface IBookkeepingTableData {
  company: string,
  sum: string,
  status: string,
  companyId: string,
  invoicinglimit: string,
  subRows: IBookkeepingSubrowData[]
}

interface IBookkeepingInvoiceData {
  company: string,
  invoice_id: string,
  invoice: string,
  invoice_format: string,
  period: string,
  invoice_date: string,
  type: string,
  original_invoice_id: string,
  sum: string,
  invoice_date_short: string,
  billed_periods: string[],
  billed_sums: number[]
}

const BOOKKEEPING_FILTER = {
  CURRENTPERIOD: 'currentperiod',
  CORRECTED: 'corrected',
  UNBILLED: 'unbilled',
  SENT: 'sent'
}

export const Bookkeeping = () => {
  const intl = useIntl();
  const tableRef = useRef<any>(null);
  const selectAllCheckboxRef = useRef<any>(null);

  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState([] as IFilterButtonData[]);
  const [data, setData] = useState([] as IBookkeepingTableData[]);
  const [sentCount, setSentCount] = useState(0);
  const [count, setCount] = useState(0);
  const [toastActive, setToastActive] = useState(false);
  const [countMax, setCountMax] = useState(0);
  const [selected, setSelected] = useState([] as IBookkeepingSubrowData[]);
  const [selectedFilter, setSelectedFilter] = useState(BOOKKEEPING_FILTER.CURRENTPERIOD);
  const [billingLimit, setBillingLimit] = useState(0);
  const [invoicingSystem, setInvoicingSystem] = useState('');
  const [confirmActive, setConfirmActive] = useState(false);
  const { addToast } = useToastHandler();

  useEffect(() => {
    getSettings().then((data: any) => {
      if (data?.error) {
          addToast(data.error, 'settings', 'fetch');
      } else if (data?.billing_limit)
        setBillingLimit(parseInt(data.billing_limit));
        setInvoicingSystem(data.invoicing_system);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (billingLimit) {
      setLoading(true);
      update()
      .finally(() => {
        setLoading(false);
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [billingLimit]);

  const update = async () => {
    await getCompanies(true).then((data: any) => {
      if (data?.error) {
        addToast(data.error, 'companies', 'fetch');
      }
      else if (data) {
        return refreshData(data);
      }
    });
  }

  const refreshData = async (companies: ICompany[]) => {
    if (companies.length > 0) {
      // setLoading(true);
      let ids = companies.map(c => parseInt(c.id ? c.id : '0'));
      await invoicingApi.getInvoiceMaterial(ids).then((data: any) => {
        if (data?.error) {
          addToast(data.error, 'bookkeeping', 'fetch');
        }
        else if (data) {
          let currentperiod = 0;
          let corrected = 0;
          let unbilled = 0;
          let sent = 0;
          if (data) {
            setData(tableData(data.grouped, data.invoices, companies));
            //Filter data
            (data.grouped as IPomReportSummaryInfo[]).forEach((g) => {
              (g.reports as IPomReportDataInfo[]).forEach((r) => {
                // NEW, ONGOING, CONFIRMED, BILLED, REOPENED, CORRECTED, REBILLED
                if(r.status === REPORTSTATUS.CONFIRMED && g.total_eur >= billingLimit) {
                  currentperiod++;
                }
                if(r.status === REPORTSTATUS.CORRECTED) {
                  corrected++;
                }
                if(r.status === REPORTSTATUS.CONFIRMED && g.total_eur < billingLimit) {
                  unbilled++;
                }
                if(r.status === REPORTSTATUS.BILLED || r.status === REPORTSTATUS.REBILLED) {
                  sent++;
                }
              });
            });
          }
          setFilters([
            { button: BOOKKEEPING_FILTER.CURRENTPERIOD, count: currentperiod, 
              filters: [{column: 'status', filterValues: [REPORTSTATUS.CONFIRMED]},
                        {column: 'invoicinglimit', filterValues: [INVOICING_LIMIT.OVER]}] },
            { button: BOOKKEEPING_FILTER.CORRECTED, count: corrected,
              filters: [{column: 'status', filterValues: [REPORTSTATUS.CORRECTED]},
                        {column: 'invoicinglimit', filterValues: [INVOICING_LIMIT.UNDER, INVOICING_LIMIT.OVER]}] },
            { button: BOOKKEEPING_FILTER.UNBILLED, count: unbilled, 
              filters: [{column: 'status', filterValues: [REPORTSTATUS.CONFIRMED]},
                        {column: 'invoicinglimit', filterValues: [INVOICING_LIMIT.UNDER]}] },
            { button: BOOKKEEPING_FILTER.SENT, count: sent, 
              filters: [{column: 'status', filterValues: [REPORTSTATUS.BILLED, REPORTSTATUS.REBILLED]},
                        {column: 'invoicinglimit', filterValues: [INVOICING_LIMIT.UNDER, INVOICING_LIMIT.OVER]}]}
          ]);
        }
      }).finally(() => {
        setLoading(false);
      });
    }
  }

  const filter = (id: number) => {
    switch(id) {
      case 0:
        return BOOKKEEPING_FILTER.CURRENTPERIOD;
      case 1:
        return BOOKKEEPING_FILTER.CORRECTED;
      case 2:
        return BOOKKEEPING_FILTER.UNBILLED;
      case 3:
        return BOOKKEEPING_FILTER.SENT;
      default:
        return BOOKKEEPING_FILTER.CURRENTPERIOD;
    }
  }

  useEffect(() => {
    // Control checkbox checked/indeterminate state...
    // If every/none item of the filtered items is selected, indeterminate = false
    // If part of items selected, indeterminate = true
    if (!selectAllCheckboxRef.current) {
      return;
    }
    if (count === 0) {
      selectAllCheckboxRef.current.checked = false;
      selectAllCheckboxRef.current.indeterminate = false;
    } else if (count < countMax ) {
      selectAllCheckboxRef.current.checked = false;
      selectAllCheckboxRef.current.indeterminate = true;
    } else {
      selectAllCheckboxRef.current.checked = true;
      selectAllCheckboxRef.current.indeterminate = false;
    }
  }, [count, selectedFilter, countMax]);

  const handleSelectAll = (change: boolean) => {
    tableRef?.current?.toggleSelectedRows(change);
  };

  const comparer = (a: IBookkeepingInvoiceData, b: IBookkeepingInvoiceData) => {
    return new Date(b.invoice_date).getTime() - new Date(a.invoice_date).getTime(); // Newest invoice first
  }

  const getInvoices = (invoiceData: IBookkeepingInvoiceData[], compId: string, period: string) => {
    let result = invoiceData.filter((i) => {
      return (((parseInt(i.company) === parseInt(compId)) && (i.period === period || (i.billed_periods?.includes(period)))))
    });
    result.sort(comparer);
    return result;
  };

  const getInvoiceView = (invoices: IBookkeepingInvoiceData[], period: string) => {
    return invoices.map( (i, index: number) => {
      let consolidated = (i.billed_periods && i.billed_periods.length > 1) ? 
          <FormattedMessage id={'billingstatus.invoice.consolidated'} values={{count: i.billed_periods.length, index: (i.billed_periods.indexOf(period)+1)}}/> : <></>;
      let type = (i.type === 'corrected' || i.type === 'refund') ? <FormattedMessage id={'billingstatus.invoice.' + i.type}/> : <></>;
      return (
        <div key={index} className="d-flex flex-row align-items-center">
          <button type="button" className="btn btn-link" onClick={() => load(i.invoice_id)}>{i.invoice_date_short} {type} {consolidated}</button>
        </div>
      );
    })
  };

  const tableData = (reports: IPomReportSummaryInfo, invoiceData: IBookkeepingInvoiceData[], companies: ICompany[]) => { 
    let data = [] as IBookkeepingTableData[];
    if(reports && Array.isArray(reports)) {
      let i = 0;
      reports.forEach((g) => {
        let name = companies.find(c => g.company === c.id)?.name;
        let subRows = [] as IBookkeepingSubrowData[];
        g.reports.forEach((r: IPomReportDataInfo) => {
          let date = new Date(r.period);
          let timestamp = getPeriodString(date);
          let monthStr = r.period.split("-")[1];
          let yearStr = r.period.split("-")[0];
          let reportingPeriod = monthStr + "/" + yearStr;
          let invoices = getInvoices(invoiceData, r.company, r.period);
          subRows.push({
            company: name ? name : '',
            period: reportingPeriod,
            state: statusView(r.status, getDeadline(date)),
            sum: formatFinCurrency(r.total_eur),
            actions: actionButtons(r.company, name ? name : '', timestamp, r.status, "/bookkeeping/companyreport", false),
            id: r.report_id,
            status: r.status,
            companyId: r.company,
            periodTimestamp: timestamp,
            billed: getInvoiceView(invoices, r.period),
            // invoicing limit from database
            invoicinglimit: Math.ceil(g.total_eur) > billingLimit ? INVOICING_LIMIT.OVER : INVOICING_LIMIT.UNDER,
          });
        });
        // Group heading row
        data[i] = {
          company: name ? name : '',
          sum: formatFinCurrency(g.total_eur),
          status: g.status,
          companyId: g.company,
          // invoicing limit from database
          invoicinglimit: Math.ceil(g.total_eur) > billingLimit ? INVOICING_LIMIT.OVER : INVOICING_LIMIT.UNDER,
          subRows: subRows
        };
        i++;
      });
    }
    return data;
  }

  const send = (format: string) => {
    if(selected.length > 0) {
      setLoading(true);
      const unique = [...new Set(selected.map(s => s.id))];
      const subrows = unique.filter(function( element ) {
        return element !== undefined;
      });
      setSentCount(subrows.length);
      invoicingApi.generateInvoices(subrows.toString(), format).then((data: any) => {
        if (data?.error) {
          addToast(data.error, 'bookkeeping', 'generate');
          setLoading(false);
        } else {
          invoicingApi.sendInvoices(subrows.toString(), format).then((data: any) => {
            if (data?.error) {
              addToast(data.error, 'bookkeeping', 'send');
              setLoading(false);
              if(data?.error.value.errorID===99 && parseInt(data?.error.value.value.success)>0) // errors in invoices, some are ok
                update().then(() => {
                  setSentCount(data?.error.value.value.success);
                  setToastActive(true);
              });
            } else {
              update().then(() => {
                setToastActive(true);
              });
            }
          });
        }
      });
    }
  }

  const load = (invoice_id: string) => {
    setLoading(true);
    invoicingApi.loadInvoice(invoice_id).then((data: any) => {
      if (data?.error) {
        addToast(data.error, 'bookkeeping', 'load');
      }
    }).finally(() => {
        setLoading(false);
    });
  }

  const toolBar = () => {
    return(
    <div className="toolbar">
      { selectedFilter !== BOOKKEEPING_FILTER.SENT &&
      <div className="select-all me-auto">
        <div className="form-check"> 
          <input className="form-check-input" type="checkbox" name="selectall" id="selectall"
                 ref={selectAllCheckboxRef} onChange={(e) => handleSelectAll(e.target.checked)}/>
          <label className="form-check-label" htmlFor="selectall"><FormattedMessage id={'bookkeeping.select.all'}/></label>
          <span className ="select-status ms-4"><FormattedMessage id={'bookkeeping.selected.count'} values={{count: count}}/></span>
        </div>
      </div>}
      <span className="item-actions ms-auto">
        { invoicingSystem.includes('netvisor') &&
          selectedFilter !== BOOKKEEPING_FILTER.SENT &&
        <button key='toolbarbuttonsendxml' className="btn btn-primary btn-send"
          disabled={selectedFilter !== BOOKKEEPING_FILTER.SENT && selected.length === 0}
          onClick={() => { setConfirmActive(true) }}>
          <i className="me-2 fa fa-paper-plane" aria-hidden="true"></i>
          <FormattedMessage id={'button.bookkeeping.send.netvisor'}/>
        </button> }
        { invoicingSystem.includes('heeros') &&
          selectedFilter !== BOOKKEEPING_FILTER.SENT &&
        <button key='toolbarbuttonsend' className="btn btn-primary btn-send"
          disabled={selectedFilter !== BOOKKEEPING_FILTER.SENT && selected.length === 0}
          onClick={() => { send('csv'); }}>
          <i className="me-2 fa fa-paper-plane" aria-hidden="true"></i>
          <FormattedMessage id={'button.bookkeeping.send'}/>
        </button>}
      </span>
    </div>);
  }

  return (
    <>
      <Spinner active={loading} />
      <ConfirmModal
        active={confirmActive}
        modalId='confirmSendModal'
        onClose={() => setConfirmActive(false)}
        title={intl.formatMessage({id: 'bookkeeping.confirm.send.invoices.title'})}
        content={<div>{intl.formatMessage({id: 'bookkeeping.confirm.send.invoices'})}</div>}
        confirmbutton={
          {
            name: intl.formatMessage({id: 'bookkeeping.confirm.send.invoices.title'}),
            class: 'btn btn-primary',
            onClick: () => { send('xml'); setConfirmActive(false); }
          }
        }
      />
      <ToastWrap active={toastActive} heading={intl.formatMessage({id: 'bookkeeping.sent'})} 
                 text={intl.formatMessage({id: 'bookkeeping.invoices.sent.info'}, {count: sentCount})} 
                 type={'success'} setActive={(active: boolean) => setToastActive(active)} />
      <div>
        <h1><FormattedMessage id="bookkeeping.bookkeeping"/></h1>
        <p className="info"><i className="fa fa-info-circle me-2"></i><FormattedMessage id="bookkeeping.statusinfo"/></p>
        <DataTable  ref={tableRef}
                    tableheadings={['company', 'period', 'state', 'sum', 'billed', 'actions']}
                    filters={filters}
                    tabledata={data} caption={'bookkeeping.tablecaption'}
                    tableType={'bookkeeping'} tableId={'reportList'} 
                    toolbar={toolBar()}
                    showFilters={true}
                    selectHeader={selectedFilter !== BOOKKEEPING_FILTER.SENT ? intl.formatMessage({id:'bookkeeping.select'}) : ''}
                    setCount={(count: number) => setCount(count)}
                    setSelected={(items: IBookkeepingSubrowData[]) => setSelected(items)}
                    setTableMaxCount={(count: number) => setCountMax(count)}
                    filterSet={selectedFilter}
                    hiddenColumns={['status', 'invoicinglimit', 'groupheading']}
                    setSelectedButton={(id: number) => setSelectedFilter(filter(id))}
                    optSortableColumns={['company', 'period']}
                    />
      </div>
    </>
  );
};